import React from 'react';
import { services } from '../data/mock';
import { Zap, Leaf, Settings, ArrowUpRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const serviceIcons = {
  1: Zap,
  2: Leaf,
  3: Settings
};

const serviceColors = {
  1: 'from-[#006633] to-[#00873D]',
  2: 'from-[#00873D] to-[#006633]',
  3: 'from-[#FF8C00] to-[#FFD700]'
};

const ServicesSection = () => {
  const { isDark } = useTheme();

  return (
    <section 
      id="services" 
      aria-labelledby="services-heading"
      className={`py-16 sm:py-24 lg:py-32 ${isDark ? 'bg-[#0A0F0D]' : 'bg-[#FAFAFA]'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section header */}
        <header className="text-center mb-10 sm:mb-16">
          <p className={`font-medium text-xs sm:text-sm uppercase tracking-[0.2em] mb-3 sm:mb-4 ${
            isDark ? 'text-green-500' : 'text-green-600'
          }`}>
            What We Do
          </p>
          <h2 id="services-heading" className="font-display font-bold leading-tight mb-4 sm:mb-6" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>Our </span>
            <span className="gradient-text">Services</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-sm sm:text-base lg:text-lg ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Quality, exceptional, cost-effective and cutting-edge solutions tailored to your needs.
          </p>
        </header>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8" role="list">
          {services.map((service) => {
            const IconComponent = serviceIcons[service.id];
            const gradientClass = serviceColors[service.id];
            
            return (
              <article 
                key={service.id}
                role="listitem"
                className={`premium-card group rounded-xl sm:rounded-2xl p-6 sm:p-8 border relative overflow-hidden ${
                  isDark 
                    ? 'bg-[#111916] border-green-900/20 hover:border-green-800/40' 
                    : 'bg-white border-gray-100 hover:border-green-200'
                }`}
              >
                {/* Hover gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} aria-hidden="true" />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${gradientClass} flex items-center justify-center mb-4 sm:mb-6 shadow-lg ${
                    service.id === 3 ? 'shadow-amber-500/20' : 'shadow-green-500/20'
                  }`}>
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                  </div>

                  {/* Title */}
                  <h3 className={`font-display font-semibold text-lg sm:text-xl mb-3 sm:mb-4 flex items-center gap-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {service.title}
                    <ArrowUpRight className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 group-hover:-translate-y-1 ${
                      isDark ? 'text-green-400' : 'text-green-600'
                    }`} aria-hidden="true" />
                  </h3>

                  {/* Description */}
                  <p className={`text-sm sm:text-base leading-relaxed mb-4 sm:mb-6 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {service.description}
                  </p>

                  {/* Outcome */}
                  <div className={`pt-4 sm:pt-6 border-t ${
                    isDark ? 'border-green-900/30' : 'border-gray-100'
                  }`}>
                    <p className={`text-xs uppercase tracking-wider mb-2 ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>Outcome</p>
                    <p className={`text-xs sm:text-sm font-medium ${
                      isDark ? 'text-green-400' : 'text-green-700'
                    }`}>
                      {service.outcome}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
